import { FulfillmentOrderUserFacingError } from "./supplierConfirmationService";

const WINDOWS_RESERVED_NAME = /^(?:con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/i;

const waitForImageLoad = (image: HTMLImageElement) => new Promise<void>((resolve, reject) => {
  const clearListeners = () => {
    image.removeEventListener("load", handleLoad);
    image.removeEventListener("error", handleError);
  };
  const handleLoad = () => {
    clearListeners();
    resolve();
  };
  const handleError = () => {
    clearListeners();
    reject(new Error("图片加载失败"));
  };
  image.addEventListener("load", handleLoad, { once: true });
  image.addEventListener("error", handleError, { once: true });
});

/** 确保我方公司公章已完成加载和解码，避免导出无章协议。 */
async function ensureCompanySealReady(source: HTMLElement): Promise<void> {
  const seal = source.querySelector<HTMLImageElement>('img[data-company-seal="true"]');
  if (!seal) {
    throw new FulfillmentOrderUserFacingError(
      "我方公司公章尚未配置，请联系管理员完成配置后重试",
    );
  }

  try {
    if (typeof seal.decode === "function") {
      await seal.decode();
    } else if (!seal.complete) {
      await waitForImageLoad(seal);
    }
    if (seal.naturalWidth <= 0) throw new Error("图片没有可用像素");
  } catch (error) {
    throw new FulfillmentOrderUserFacingError(
      "我方公司公章加载失败，请检查图片配置后重试",
      { cause: error },
    );
  }
}

/** 清理 Windows 非法文件名并统一 PDF 扩展名。 */
function normalizePdfFileName(fileName: string): string {
  const withoutControlCharacters = Array.from(
    fileName.trim(),
    (character) => character.charCodeAt(0) < 32 ? "_" : character,
  ).join("");
  const normalized = withoutControlCharacters
    .replace(/[<>:"/\\|?*]/g, "_")
    .replace(/[ .]+$/g, "")
    .replace(/\.pdf$/i, "");
  const safeName = WINDOWS_RESERVED_NAME.test(normalized) ? `_${normalized}` : normalized;
  return `${safeName || "确认协议"}.pdf`;
}

/** 将当前精确版本合同正文生成并下载为 PDF。 */
export async function downloadFulfillmentOrderAgreementPdf(
  source: HTMLElement,
  fileName: string,
  assertCurrent?: () => void,
): Promise<void> {
  if (!(source instanceof HTMLElement)) throw new Error("协议正文尚未就绪");
  await ensureCompanySealReady(source);
  if ("fonts" in document) await document.fonts.ready;
  assertCurrent?.();

  const clone = source.cloneNode(true) as HTMLElement;
  const mount = document.createElement("div");
  mount.classList.add("fulfillment-order-agreement-document--pdf");
  clone.classList.add("fulfillment-order-agreement-document--pdf-content");
  mount.setAttribute("aria-hidden", "true");
  mount.style.setProperty("top", "0", "important");
  mount.style.setProperty("left", "0", "important");
  mount.style.setProperty("display", "block", "important");
  mount.style.setProperty("visibility", "visible", "important");
  mount.style.setProperty("opacity", "1", "important");
  mount.style.setProperty("pointer-events", "none", "important");
  mount.style.setProperty("z-index", "-1", "important");

  clone.style.setProperty("position", "relative", "important");
  clone.style.setProperty("top", "0", "important");
  clone.style.setProperty("left", "0", "important");
  clone.style.setProperty("display", "block", "important");
  clone.style.setProperty("visibility", "visible", "important");
  clone.style.setProperty("opacity", "1", "important");
  clone.style.setProperty("width", "190mm", "important");
  clone.style.setProperty("min-width", "190mm", "important");
  clone.style.setProperty("max-width", "none", "important");
  clone.style.setProperty("overflow", "visible", "important");
  clone.style.setProperty("border", "0", "important");
  clone.style.setProperty("border-radius", "0", "important");
  clone.style.setProperty("box-shadow", "none", "important");

  mount.append(clone);
  document.body.prepend(mount);

  try {
    const { default: html2pdf } = await import("html2pdf.js");
    assertCurrent?.();
    const options = {
      margin: [10, 10, 10, 10] as [number, number, number, number],
      filename: normalizePdfFileName(fileName),
      image: { type: "jpeg" as const, quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        scrollX: 0,
        scrollY: 0,
      },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" as const },
      pagebreak: {
        mode: ["css", "legacy"],
        avoid: [
          "tr",
          ".fulfillment-order-agreement-document-parties",
          ".fulfillment-order-agreement-document-signatures",
        ],
      },
    };
    const worker = html2pdf().set(options).from(clone).toPdf();
    await worker.get("pdf");
    assertCurrent?.();
    await worker.save();
  } finally {
    mount.remove();
  }
}
